/**
 * The URL is the application state.
 *
 * Filters, the selected catalog revision, the observation context and the as-of
 * time all live in the query string, so any view a person is looking at can be
 * copied to someone else and reopened unchanged. Nothing view-shaped is kept in
 * a store that the address bar does not know about.
 */

import { useCallback, useEffect, useState } from "react";

export type ViewName = "prices" | "changes" | "model" | "sources";

export interface ExplorerState {
  readonly view: ViewName;
  readonly q: string;
  readonly billing_provider_id: string;
  readonly offering_id: string;
  readonly source_id: string;
  readonly catalog_revision_id: string;
  readonly observation_sequence: string;
  readonly observation_cutoff_utc: string;
  readonly at_utc: string;
  readonly from_utc: string;
  readonly to_utc: string;
  readonly direction: string;
  readonly bucket: string;
  readonly freshness: string;
  readonly min_input_tokens: string;
}

export const EMPTY_STATE: ExplorerState = {
  view: "prices",
  q: "",
  billing_provider_id: "",
  offering_id: "",
  source_id: "",
  catalog_revision_id: "",
  observation_sequence: "",
  observation_cutoff_utc: "",
  at_utc: "",
  from_utc: "",
  to_utc: "",
  direction: "",
  bucket: "",
  freshness: "",
  min_input_tokens: "",
};

function readState(): ExplorerState {
  const params = new URLSearchParams(window.location.search);
  const view = params.get("view");
  return {
    ...EMPTY_STATE,
    ...Object.fromEntries(
      Object.keys(EMPTY_STATE)
        .filter((key) => key !== "view")
        .map((key) => [key, params.get(key) ?? ""]),
    ),
    view: (["prices", "changes", "model", "sources"] as const).includes(view as ViewName)
      ? (view as ViewName)
      : "prices",
  };
}

export interface UpdateOptions {
  /**
   * Rewrite the current history entry instead of adding one. For refinements
   * such as a typed filter, where each intermediate value is not a place worth
   * going back to.
   */
  readonly replace?: boolean;
}

export type UpdateState = (patch: Partial<ExplorerState>, options?: UpdateOptions) => void;

export function useExplorerState(): [ExplorerState, UpdateState] {
  const [state, setState] = useState<ExplorerState>(readState);

  useEffect(() => {
    const onPopState = (): void => setState(readState());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const update = useCallback<UpdateState>((patch, options = {}) => {
    setState((current) => {
      const next = { ...current, ...patch };
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(next)) {
        if (value !== "" && !(key === "view" && value === "prices")) params.set(key, String(value));
      }
      const search = params.toString();
      const url = `${window.location.pathname}${search === "" ? "" : `?${search}`}`;
      if (options.replace) window.history.replaceState(null, "", url);
      else window.history.pushState(null, "", url);
      return next;
    });
  }, []);

  return [state, update];
}

/** The context parameters to send with every request, as the URL holds them. */
export function contextParams(state: ExplorerState): Record<string, string> {
  const params: Record<string, string> = {};
  if (state.catalog_revision_id) params.catalog_revision_id = state.catalog_revision_id;
  if (state.observation_sequence) params.observation_sequence = state.observation_sequence;
  if (state.observation_cutoff_utc) params.observation_cutoff_utc = state.observation_cutoff_utc;
  return params;
}
